import React from 'react';
import Layout from '../components/Layout';
import SEO from '../components/Seo';
import Button from '../components/common/Button';
import { graphql, useStaticQuery, Link } from 'gatsby';
import { extractContent } from '../util';
import ContentfulContent from '../components/common/ContentfulContent';
import { UsersIcon, AcademicCapIcon } from '@heroicons/react/outline';

const ServiceBlockLarge = () => {};

const isInternalLink = (url: string) => {
  return typeof url === 'string' && url.startsWith('/');
};

/** Renders a link styled as a button - avoids nesting <button> inside <a> which breaks clicks */
const CtaLink: React.FC<{
  to?: string;
  href?: string;
  children: React.ReactNode;
  className?: string;
}> = ({ to, href, children, className }) => {
  const base =
    'rounded-xl px-4 py-2 inline-flex items-center justify-center sans-serif';
  const classes = className ? `${base} ${className}` : base;
  if (to) {
    return <Link to={to} className={classes}>{children}</Link>;
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={classes}
    >
      {children}
    </a>
  );
};

const Services: React.FC = () => {
  const data = useStaticQuery(graphql`
    {
      allContentfulServicesPage(filter: { node_locale: { eq: "en-US" } }) {
        edges {
          node {
            id
            heroPill
            headerText
            heroCopy {
              heroCopy
            }
            heroImages {
              fluid {
                src
              }
            }
            serviceBlocks {
              theme
              headerText
              headerIcon
              body {
                body
              }
              bulletedList1 {
                bulletedList1
              }
              bulletedList2 {
                bulletedList2
              }
              bulletedList3 {
                bulletedList3
              }
              bulletedList4 {
                bulletedList4
              }
              useCtaButton
              ctaButtonText
              ctaButtonLink
              image1 {
                fluid {
                  src
                }
              }
              image2 {
                fluid {
                  src
                }
              }
              image3 {
                fluid {
                  src
                }
              }
              image4 {
                fluid {
                  src
                }
              }
              blockPlacement
            }
            testimonialSectionTitle
            testimonialSectionBody
            testimonialMetric1Value
            testimonialMetric1Label
            testimonialMetric2Value
            testimonialMetric2Label
            testimonial1Title
            testimonial1Body
            testimonial1Source
            testimonial2Title
            testimonial2Body
            testimonial2Source
            testimonial3Title
            testimonial3Body
            testimonial3Source
            testimonial4Title
            testimonial4Body
            testimonial4Source
            testimonial5Title
            testimonial5Body
            testimonial5Source
            testimonial6Title
            testimonial6Body
            testimonial6Source
            ctaBlock {
              title
              body {
                body
              }
            }
          }
        }
      }
    }
  `);

  let content = null;

  content = extractContent(data.allContentfulServicesPage);

  if (content.length > 0) {
    content = content[0];
  }
  if (!content) {
    return <div>Loading...</div>;
  }
  const renderBulletedColumn = (content: any) => {
    return (
      <div className="space-y-1 text-white">
        <ContentfulContent content={content} variant="LIGHT_ON_DARK" />
      </div>
    );
  };

  const renderLargeBlock = (block: any, idx: number) => {
    const images = [block.image1, block.image2, block.image3, block.image4]
      .filter(Boolean)
      .map((img: any) => img?.fluid?.src || img);
    return (
      <section className="py-20 relative" key={`large-${idx}`}>
        <div className="container mx-auto px-6" id="large">
          <div className="max-w-7xl mx-auto space-y-6">
            <div className="magazine-card creative-shadow magazine-gradient text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-12 -translate-x-12"></div>
              <div className="relative p-8 lg:p-12">
                <div className="flex items-start justify-between mb-8">
                  <div className="w-16 h-16 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center">
                    <UsersIcon className="w-8 h-8 text-white" />
                  </div>
                </div>
                <h2 className="text-3xl lg:text-4xl font-bold mb-6 sans-serif">
                  {block.headerText}
                </h2>
                <div className="text-white/90 mb-6 leading-relaxed max-w-4xl sans-serif text-white">
                  <ContentfulContent
                    content={block.body?.body}
                    variant="LIGHT_ON_DARK"
                  />
                </div>
                {(() => {
                  const lists = [
                    block.bulletedList1?.bulletedList1,
                    block.bulletedList2?.bulletedList2,
                    block.bulletedList3?.bulletedList3,
                    block.bulletedList4?.bulletedList4
                  ].filter(Boolean);
                  const cols = Math.min(Math.max(lists.length || 1, 1), 4);
                  const gridColsClass =
                    cols === 1
                      ? 'grid-cols-1'
                      : cols === 2
                        ? 'grid-cols-2'
                        : cols === 3
                          ? 'grid-cols-3'
                          : 'grid-cols-4';
                  return (
                    <div
                      className={`grid ${gridColsClass} gap-6 mb-8 text-white`}
                    >
                      {lists.map((lst: any, i: number) => (
                        <div key={i}>{renderBulletedColumn(lst)}</div>
                      ))}
                    </div>
                  );
                })()}
                {block.useCtaButton && block.ctaButtonText ? (
                  <div className="mb-8">
                    {block.ctaButtonLink ? (
                      isInternalLink(block.ctaButtonLink) ? (
                        <CtaLink
                          to={block.ctaButtonLink}
                          className="bg-white text-primary hover:bg-white/90"
                        >
                          {block.ctaButtonText}
                        </CtaLink>
                      ) : (
                        <CtaLink
                          href={block.ctaButtonLink}
                          className="bg-white text-primary hover:bg-white/90"
                        >
                          {block.ctaButtonText}
                        </CtaLink>
                      )
                    ) : (
                      <Button
                        variant="inverse"
                        size="lg"
                        className="bg-white text-primary hover:bg-white/90"
                        text={block.ctaButtonText}
                      />
                    )}
                  </div>
                ) : null}
                {images.length ? (
                  <div className="images-4 mt-8">
                    {images.map((src: string, i: number) => (
                      <div key={i}>
                        <img
                          src={src}
                          alt={`Block image ${i + 1}`}
                          className="w-full h-72 object-cover rounded-xl shadow-lg"
                        />
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  };

  const renderSmallBlock = (block: any, idx: number) => {
    // Alternate backgrounds between the small blocks
    const wrapperId = `small-${idx + 1}`;
    const presentImages: string[] = [block.image1, block.image2]
      .filter(Boolean)
      .map((img: any) => img?.fluid?.src || img)
      .filter(Boolean);
    const hasImages = presentImages.length > 0;
    return (
      <section
        className={idx % 2 === 0 ? 'py-12 bg-white' : 'py-20 pattern-dots'}
        key={`small-${idx}`}
      >
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div
              className={`grid ${hasImages ? 'lg:grid-cols-2' : 'lg:grid-cols-1'} gap-8 items-center`}
            >
              {idx % 2 === 0 ? (
                <div
                  id={wrapperId}
                  className="magazine-card bg-white creative-shadow border border-primary/10 p-8 lg:p-10"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 bg-primary bg-opacity-10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <AcademicCapIcon className="w-8 h-8 text-primary" />
                    </div>
                    <h2 className="text-2xl lg:text-3xl font-bold text-primary">
                      {block.headerText}
                    </h2>
                  </div>
                  <div className="text-foreground/80 text-base leading-relaxed mb-6">
                    <ContentfulContent content={block.body?.body} />
                  </div>
                  {block.useCtaButton && block.ctaButtonText ? (
                    block.ctaButtonLink ? (
                      isInternalLink(block.ctaButtonLink) ? (
                        <CtaLink
                          to={block.ctaButtonLink}
                          className="bg-primary text-white hover:bg-primary/90"
                        >
                          {block.ctaButtonText}
                        </CtaLink>
                      ) : (
                        <CtaLink
                          href={block.ctaButtonLink}
                          className="bg-primary text-white hover:bg-primary/90"
                        >
                          {block.ctaButtonText}
                        </CtaLink>
                      )
                    ) : (
                      <Button
                        className="bg-primary text-white hover:bg-primary/90"
                        text={block.ctaButtonText}
                      />
                    )
                  ) : null}
                </div>
              ) : null}
              {hasImages ? (
                <div
                  className={`grid ${presentImages.length === 1 ? 'grid-cols-1' : 'grid-cols-2'} gap-8`}
                >
                  {presentImages.map((src: string, i: number) => (
                    <img
                      key={i}
                      src={src}
                      alt={`Small block image ${i + 1}`}
                      className={
                        i === 1
                          ? 'w-full h-full object-cover rounded-xl shadow-lg mt-8'
                          : 'w-full h-full object-cover rounded-xl shadow-lg'
                      }
                    />
                  ))}
                </div>
              ) : null}
              {idx % 2 !== 0 ? (
                <div
                  id={wrapperId}
                  className="magazine-card bg-white creative-shadow p-8 lg:p-10"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 bg-primary bg-opacity-10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <AcademicCapIcon className="w-8 h-8 text-primary" />
                    </div>
                    <h2 className="text-2xl lg:text-3xl font-bold text-primary">
                      {block.headerText}
                    </h2>
                  </div>
                  <div className="text-foreground/80 text-base leading-relaxed mb-6">
                    <ContentfulContent content={block.body?.body} />
                  </div>
                  <div className="flex flex-wrap gap-4">
                    {block.useCtaButton && block.ctaButtonText ? (
                      block.ctaButtonLink ? (
                        isInternalLink(block.ctaButtonLink) ? (
                          <CtaLink
                            to={block.ctaButtonLink}
                            className="bg-transparent border border-primary text-primary hover:bg-primary hover:text-white"
                          >
                            {block.ctaButtonText}
                          </CtaLink>
                        ) : (
                          <CtaLink
                            href={block.ctaButtonLink}
                            className="bg-transparent border border-primary text-primary hover:bg-primary hover:text-white"
                          >
                            {block.ctaButtonText}
                          </CtaLink>
                        )
                      ) : (
                        <Button
                          className="bg-transparent border-primary hover:bg-primary hover:text-white"
                          text={block.ctaButtonText}
                        />
                      )
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </section>
    );
  };

  const renderMediumBlock = (block: any, idx: number) => {
    const leftImg = block.image1?.fluid?.src || '/images/assessment-1.jpg';
    return (
      <section className="py-20 magazine-bg" key={`medium-${idx}`}>
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div
              id="medium"
              className="magazine-card bg-white creative-shadow overflow-hidden"
            >
              <div className="grid lg:grid-cols-2 gap-8 items-start">
                <div className="p-8 lg:p-12">
                  <img
                    src={leftImg}
                    alt={block.headerText}
                    className="w-full rounded-xl shadow-lg"
                  />
                  {block.useCtaButton && block.ctaButtonText ? (
                    <div className="mt-6">
                      {block.ctaButtonLink ? (
                        isInternalLink(block.ctaButtonLink) ? (
                          <CtaLink
                            to={block.ctaButtonLink}
                            className="w-full bg-primary text-white hover:bg-primary/90"
                          >
                            {block.ctaButtonText}
                          </CtaLink>
                        ) : (
                          <CtaLink
                            href={block.ctaButtonLink}
                            className="w-full bg-primary text-white hover:bg-primary/90"
                          >
                            {block.ctaButtonText}
                          </CtaLink>
                        )
                      ) : (
                        <Button
                          size="lg"
                          className="w-full"
                          text={block.ctaButtonText}
                        />
                      )}
                    </div>
                  ) : null}
                </div>
                <div className="p-8 lg:p-12 lg:pl-0">
                  <h2 className="text-2xl lg:text-3xl font-bold text-primary mb-6 leading-tight">
                    {block.headerText}
                  </h2>
                  <div className="text-foreground/80 text-base leading-relaxed mb-6">
                    <ContentfulContent content={block.body?.body} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  };
  return (
    <Layout>
      <SEO title="Services" />
      <div className="min-h-screen bg-background overflow-hidden sans-serif">
        <section className="py-16 bg-white border-b">
          <div className="container mx-auto px-6">
            <div className="max-w-7xl mx-auto text-center ">
              <div className="inline-block px-4 py-1.5 bg-gray-200 text-primary text-sm font-semibold rounded-full mb-6 sans-serif">
                {content.heroPill}
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold text-primary mb-6 text-balance sans-serif">
                {content.headerText}
              </h1>
              <div className="w-24 h-1 bg-gradient-to-r from-primary to-primary/40 mx-auto mb-8 rounded-full"></div>
              <div className="text-lg text-foreground/70 leading-relaxed max-w-6xl mx-auto mb-12 sans-serif">
                <ContentfulContent content={content.heroCopy.heroCopy} />
              </div>
              <div className="flex justify-center space-x-6 max-w-4xl mx-auto">
                {content.heroImages && content.heroImages.length > 0
                  ? content.heroImages.map((img: any, idx: number) => (
                      <div
                        className="relative overflow-hidden rounded-xl shadow-lg group w-1/3"
                        key={img.id || idx}
                      >
                        <img
                          src={img.fluid.src}
                          alt={img.altText || `Hero image ${idx + 1}`}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      </div>
                    ))
                  : content.heroImage && (
                      <div className="relative overflow-hidden rounded-xl shadow-lg group">
                        <img
                          src={content.heroImage.fluid.src}
                          alt="Teacher reading to diverse group of children"
                          className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      </div>
                    )}
              </div>
            </div>
          </div>
        </section>

        {(content.serviceBlocks || []).map((block: any, idx: number) => {
          const theme = (block.theme || '').toLowerCase();
          if (theme === 'large') return renderLargeBlock(block, idx);
          if (theme === 'medium') return renderMediumBlock(block, idx);
          if (theme === 'small') return renderSmallBlock(block, idx);
          return null;
        })}

        <section className="py-20 relative">
          <div className="container mx-auto px-6">
            <div className="max-w-7xl mx-auto">
              <div className="mixed-layout items-center">
                <div className="col-span-12 lg:col-span-6 space-y-8">
                  <h2 className="text-3xl lg:text-4xl font-bold text-balance">
                    {content.testimonialSectionTitle || (
                      <>
                        Real Impact in
                        <span className="block text-primary">
                          Real Classrooms
                        </span>
                      </>
                    )}
                  </h2>
                  <p className="text-lg text-muted-foreground leading-relaxed">
                    {content.testimonialSectionBody ||
                      'Our research-based approaches have been tested and refined in hundreds of partner schools, ensuring every strategy we teach has proven effectiveness.'}
                  </p>
                  <div className="grid grid-cols-2 gap-6">
                    <div className="magazine-card creative-shadow p-6 text-center interactive-element">
                      <div className="text-3xl font-bold text-primary mb-2">
                        {content.testimonialMetric1Value || '85%'}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {content.testimonialMetric1Label ||
                          'Reading Improvement'}
                      </div>
                    </div>
                    <div className="magazine-card creative-shadow p-6 text-center interactive-element">
                      <div className="text-3xl font-bold text-primary mb-2">
                        {content.testimonialMetric2Value || '92%'}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {content.testimonialMetric2Label ||
                          'Teacher Satisfaction'}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-span-12 lg:col-span-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      {
                        title: content.testimonial1Title,
                        body: content.testimonial1Body,
                        source: content.testimonial1Source
                      },
                      {
                        title: content.testimonial2Title,
                        body: content.testimonial2Body,
                        source: content.testimonial2Source
                      },
                      {
                        title: content.testimonial3Title,
                        body: content.testimonial3Body,
                        source: content.testimonial3Source
                      },
                      {
                        title: content.testimonial4Title,
                        body: content.testimonial4Body,
                        source: content.testimonial4Source
                      },
                      {
                        title: content.testimonial5Title,
                        body: content.testimonial5Body,
                        source: content.testimonial5Source
                      },
                      {
                        title: content.testimonial6Title,
                        body: content.testimonial6Body,
                        source: content.testimonial6Source
                      }
                    ]
                      .filter(
                        testimonial => testimonial.title && testimonial.body
                      )
                      .map((testimonial, index) => (
                        <div
                          key={index}
                          className={`magazine-card creative-shadow p-6 bg-white interactive-element ${
                            index % 2 === 1 ? 'mt-8 md:mt-0' : ''
                          }`}
                        >
                          <div className="text-sm font-semibold text-primary mb-3">
                            {testimonial.title}
                          </div>
                          <p className="text-sm text-muted-foreground leading-relaxed italic">
                            "{testimonial.body}"
                          </p>
                          <p className="text-sm text-muted-foreground leading-relaxed mt-2">
                            - {testimonial.source}
                          </p>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 magazine-gradient text-white relative overflow-hidden">
          <div className="absolute top-0 left-0 w-64 h-64 bg-white/5 rounded-full -translate-x-32 -translate-y-32"></div>
          <div className="absolute bottom-0 right-0 w-48 h-48 bg-white/10 rounded-full translate-x-24 translate-y-24"></div>
          <div className="container mx-auto px-6 relative">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-8">
                {content.ctaBlock.title}
              </h2>
              <p className="text-xl text-white/90 mb-12 leading-relaxed max-w-4xl mx-auto">
                <ContentfulContent
                  content={content.ctaBlock.body.body}
                  variant="LIGHT_ON_DARK"
                />
              </p>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default Services;
